import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { BreathingPatternDto } from '../../api';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';
import {
  BreathingStepKey,
  breathingPositionAt,
  buildBreathingPlan,
} from './breathingPlan';

/**
 * Animated breathing pacer (MI-004).
 *
 * The movement is derived from the session clock rather than driven by its own
 * timer: the caller passes the seconds elapsed inside the breathing phase, so
 * pausing freezes the circle where it is and resuming carries on without a jump.
 */

const STEP_LABEL_KEYS: Record<BreathingStepKey, string> = {
  inhale: 'meditationPacerInhale',
  hold: 'meditationPacerHold',
  exhale: 'meditationPacerExhale',
  holdOut: 'meditationPacerHoldOut',
};

export interface BreathingPacerProps {
  pattern?: BreathingPatternDto;
  /** Seconds elapsed inside the breathing phase, taken from the session clock. */
  elapsedSeconds: number;
  isRunning: boolean;
  /** Overrides the operating system preference. Used by tests. */
  reducedMotion?: boolean;
}

const BreathingPacer: React.FC<BreathingPacerProps> = ({
  pattern,
  elapsedSeconds,
  isRunning,
  reducedMotion,
}) => {
  const { t } = useTranslation();
  const systemPrefersReducedMotion = usePrefersReducedMotion();
  const plan = useMemo(() => buildBreathingPlan(pattern), [pattern]);

  if (!plan) {
    return null;
  }

  const withoutMotion = reducedMotion ?? systemPrefersReducedMotion;
  const position = breathingPositionAt(plan, elapsedSeconds);
  const stepLabel = t(STEP_LABEL_KEYS[position.step.key]);
  const secondsLeft = Math.ceil(position.stepRemainingSeconds);

  return (
    <div
      className="flex flex-col items-center gap-3"
      data-testid="breathing-pacer"
      data-step={position.step.key}
      data-cycle={position.cycle}
    >
      <div className="relative flex h-40 w-40 items-center justify-center">
        <span
          className="absolute inset-0 rounded-full border border-indigo-200"
          aria-hidden="true"
        />
        {withoutMotion ? (
          <span
            className="absolute h-24 w-24 rounded-full bg-indigo-100"
            aria-hidden="true"
            data-testid="breathing-pacer-static"
          />
        ) : (
          <motion.span
            className="absolute h-40 w-40 rounded-full bg-indigo-200/70"
            aria-hidden="true"
            data-testid="breathing-pacer-circle"
            initial={false}
            animate={{ scale: isRunning ? position.step.toScale : position.scale }}
            transition={
              isRunning
                ? { duration: position.stepRemainingSeconds, ease: 'linear' }
                : { duration: 0 }
            }
          />
        )}
        <div className="relative text-center">
          <p className="text-sm font-medium text-slate-800">{stepLabel}</p>
          {withoutMotion && (
            <p
              className="text-2xl font-semibold tabular-nums text-indigo-700"
              data-testid="breathing-pacer-countdown"
            >
              {t('meditationPacerSecondsLeft', { seconds: secondsLeft })}
            </p>
          )}
        </div>
      </div>
      <p className="text-xs uppercase tracking-wide text-indigo-500">
        {t('meditationPacerCycleCounter', { current: position.cycle, total: plan.cycles })}
      </p>
    </div>
  );
};

export default BreathingPacer;
